<template>
    <div class="mb-3">
        <label class="form-label">{{ field.label }}</label>

        <input v-if="field.type === 'text'"
               type="text"
               class="form-control"
               v-model="textValue">

        <input v-else-if="field.type === 'number'"
               type="number"
               class="form-control"
               v-model.number="numberValue">

        <input v-else-if="field.type === 'date'"
               type="date"
               class="form-control"
               v-model="dateValue">

        <select v-else-if="field.type === 'boolean'"
                class="form-control"
                v-model="boolValue">
            <option :value="null">---</option>
            <option :value="yesValue">כן</option>
            <option :value="noValue">לא</option>
        </select>

        <Multiselect v-else-if="field.type === 'single_select'"
                     selectedLabel=""
                     deselect-label=""
                     label="label"
                     track-by="id"
                     :multiple="false"
                     placeholder=""
                     selectLabel=""
                     v-model="singleSelectObj"
                     :options="visibleOptions">
            <template slot="noResult">לא נמצאו תוצאות</template>
            <template slot="noOptions">אין אפשרויות</template>
        </Multiselect>

        <Multiselect v-else-if="field.type === 'multi_select'"
                     selectedLabel=""
                     deselect-label=""
                     label="label"
                     track-by="id"
                     :multiple="true"
                     :close-on-select="false"
                     placeholder=""
                     selectLabel=""
                     v-model="multiSelectArr"
                     :options="visibleOptions">
            <template slot="noResult">לא נמצאו תוצאות</template>
            <template slot="noOptions">אין אפשרויות</template>
        </Multiselect>

        <Multiselect v-else-if="field.type === 'entity_ref'"
                     selectedLabel=""
                     deselect-label=""
                     label="name"
                     track-by="id"
                     :multiple="false"
                     placeholder=""
                     selectLabel=""
                     v-model="entityRefObj"
                     :options="entityRefOptions">
            <template slot="noResult">לא נמצאו תוצאות</template>
            <template slot="noOptions">אין אפשרויות</template>
        </Multiselect>
    </div>
</template>

<script>

// One custom field input, working against either storage model:
//   - event fields ("slots"): value lives in owner[slotFamily_slot_N]; multi_select
//     selections live in owner['_cf_multi_' + field.id] (persisted separately via
//     the memberships endpoint — see customFieldsMixin).
//   - contact/meeting/task fields ("json"): value lives in owner.customFields[field.id],
//     saved together with the owner row by the regular save flow.
import Multiselect from 'vue-multiselect';

export default {
    name: 'CustomFieldInput',
    components: { Multiselect },
    props: {
        field: { type: Object, required: true },
        owner: { type: Object, required: true }
    },
    computed: {

        isJsonStorage: function() {
            return !!this.field.appliesTo && this.field.appliesTo !== 'event';
        },

        slotCol: function() {
            return this.field.slotFamily + '_slot_' + this.field.slotIndex;
        },

        storedValue: {
            get: function() {
                if (this.isJsonStorage) {
                    var bag = this.owner.customFields;
                    if (!bag) return null;
                    var v = bag[this.field.id];
                    return typeof v === 'undefined' ? null : v;
                }
                var raw = this.owner[this.slotCol];
                return typeof raw === 'undefined' ? null : raw;
            },
            set: function(v) {
                if (this.isJsonStorage) {
                    if (!this.owner.customFields)
                        this.$set(this.owner, 'customFields', {});
                    this.$set(this.owner.customFields, this.field.id, v);
                    return;
                }
                this.$set(this.owner, this.slotCol, v);
            }
        },

        textValue: {
            get: function() { return this.storedValue === null ? '' : this.storedValue; },
            set: function(v) { this.storedValue = v === '' ? null : v; }
        },

        numberValue: {
            get: function() { return this.storedValue; },
            set: function(v) { this.storedValue = (v === '' || v === null || typeof v === 'undefined' || isNaN(v)) ? null : v; }
        },

        // API date values may arrive as full ISO datetimes; <input type=date> needs YYYY-MM-DD
        dateValue: {
            get: function() {
                var v = this.storedValue;
                if (!v) return '';
                return String(v).slice(0, 10);
            },
            set: function(v) { this.storedValue = v ? v : null; }
        },

        // slot columns are tinyint (1/0), json storage keeps real booleans
        yesValue: function() { return this.isJsonStorage ? true : 1; },
        noValue: function() { return this.isJsonStorage ? false : 0; },

        boolValue: {
            get: function() {
                var v = this.storedValue;
                if (v === null) return null;
                if (this.isJsonStorage) return !!v;
                return v ? 1 : 0;
            },
            set: function(v) { this.storedValue = v; }
        },

        // full option list (incl. hidden — needed to resolve already-stored values)
        allOptions: function() {
            if (this.field.type !== 'single_select' && this.field.type !== 'multi_select') return [];
            if (this.isJsonStorage)
                return this.$store.state.customFieldOptions[this.field.id] || [];
            var tableKey = this.field.slotFamily + '_slot_' + this.field.slotIndex;
            return this.$store.state.customFieldSlotTables[tableKey] || [];
        },

        visibleOptions: function() {
            return this.allOptions.filter(function(o) { return !o.isHidden; });
        },

        entityRefOptions: function() {
            var map = {
                customer: 'emptyAutocomplete',
                event_class: 'eventClasses',
                crew_member: 'crewMembers',
                event_place: 'eventPlaces',
                lead_source: 'leadSources',
                other_vendor: 'otherVendors'
            };
            var list = this.$store.state[map[this.field.entityRefTarget]] || [];
            return list.filter(function(x) { return !x.hidden; });
        },

        singleSelectObj: {
            get: function() {
                var id = this.storedValue;
                if (!id) return null;
                return this.allOptions.find(function(o) { return o.id == id; }) || null;
            },
            set: function(val) { this.storedValue = val ? val.id : null; }
        },

        entityRefObj: {
            get: function() {
                var id = this.storedValue;
                if (!id) return null;
                var map = {
                    customer: 'emptyAutocomplete',
                    event_class: 'eventClasses',
                    crew_member: 'crewMembers',
                    event_place: 'eventPlaces',
                    lead_source: 'leadSources',
                    other_vendor: 'otherVendors'
                };
                var list = this.$store.state[map[this.field.entityRefTarget]] || [];
                return list.find(function(x) { return x.id == id; }) || null;
            },
            set: function(val) { this.storedValue = val ? val.id : null; }
        },

        multiSelectArr: {
            get: function() {
                if (!this.isJsonStorage)
                    return this.owner['_cf_multi_' + this.field.id] || [];
                var ids = this.storedValue || [];
                var options = this.allOptions;
                return ids
                    .map(function(id) { return options.find(function(o) { return o.id == id; }); })
                    .filter(function(o) { return !!o; });
            },
            set: function(val) {
                var arr = val || [];
                if (!this.isJsonStorage) {
                    this.$set(this.owner, '_cf_multi_' + this.field.id, arr);
                    return;
                }
                this.storedValue = arr.map(function(o) { return o.id; });
            }
        }

    }
};
</script>
